import React, { useState } from 'react';
import api from '../services/api';
import { Key, AlertCircle } from 'lucide-react';
import './PasswordResetModal.css';

/**
 * Password Reset Modal - shown when user must reset password (e.g., after temp password).
 */
export const PasswordResetModal = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.old_password || !formData.new_password || !formData.confirm_password) {
      setError('All fields are required');
      return;
    }

    if (formData.new_password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/[a-zA-Z]/.test(formData.new_password) || !/[0-9]/.test(formData.new_password)) {
      setError('Password must contain at least one letter and one number');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await api.post('/v1/auth/reset-password/', {
        old_password: formData.old_password,
        new_password: formData.new_password,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="password-reset-modal-overlay">
      <div className="password-reset-modal">
        <div className="password-reset-header">
          <Key size={24} />
          <h2>Password Reset Required</h2>
        </div>
        <p className="password-reset-description">
          You must reset your password before continuing. Please enter your current password and choose a new secure password.
        </p>

        {error && (
          <div className="password-reset-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Current Password *</label>
            <input
              type="password"
              value={formData.old_password}
              onChange={(e) => setFormData({ ...formData, old_password: e.target.value })}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>New Password *</label>
            <input
              type="password"
              value={formData.new_password}
              onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
              required
              minLength={8}
            />
            <small>Must be at least 8 characters with at least one letter and one number</small>
          </div>

          <div className="form-group">
            <label>Confirm New Password *</label>
            <input
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              required
              minLength={8}
            />
          </div>

          <div className="password-reset-actions">
            {onCancel && (
              <button type="button" onClick={onCancel} className="btn-secondary">
                Cancel
              </button>
            )}
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

